/**
 * Shared surfaces for the free calculators (pay T1039, GST T1040), so every tool reads as one family:
 * inputs sit on the neutral card, the answer sits on a success tint mixed from existing tokens (no new colour),
 * and the answer figure uses the success accent. Contrast: success on the tint is >= 4.5:1 in light and dark.
 * Design: docs/design/html/flows/web-landing-tool-pay.mjs (`.lp-tool-*` in the flow CSS).
 */

/**
 * Two panels side by side from md, stacked on phones (input first). Both panels are flex columns in a stretched
 * grid row, so they share one height; put `toolPanelFoot` on the last block of each to pin it to the bottom
 * (no fixed pixel heights). Template for the GST, HECS and Medicare calculators (Owner, 2026-10-31).
 */
export const toolPanels = "grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]"

/** Input panel: neutral card surface. */
export const toolInputPanel =
  "bg-card border-border flex flex-col gap-4 rounded-xl border p-4 md:p-6"

/** Result panel: success tint over the card colour, so it stays opaque on any page background. */
export const toolResultPanel =
  "border-success/30 bg-[color-mix(in_oklab,var(--success)_8%,var(--card))] flex scroll-mt-16 flex-col gap-3 rounded-xl border p-4 md:p-6"

/** Short heading at the top of each panel ("Your pay", "Your take-home pay"). */
export const toolPanelHeading = "text-lg font-semibold"

/** Slot that holds the answer figure or the error text at a fixed height, so an update never moves the layout. */
export const toolResultSlot = "flex min-h-12 items-center md:min-h-15"

/** The answer figure in the result accent colour. */
export const toolResultFigure =
  "text-success text-4xl leading-none font-bold tracking-tight tabular-nums md:text-5xl"

/** Bordered group around the hero amount and anything attached to it (period chips, hours); one focus ring. */
export const toolHeroBox =
  "border-input bg-card focus-within:border-ring focus-within:ring-ring/50 overflow-hidden rounded-lg border focus-within:ring-[3px]"

/** The one amount that drives the tool, inside `toolHeroBox` (56 px row); add a size class for long values. */
export const toolHeroInput =
  "w-full min-w-0 flex-1 bg-transparent font-semibold tracking-tight tabular-nums outline-none"

/** Currency prefix in front of the hero input. */
export const toolHeroPrefix = "text-muted-foreground text-xl font-semibold"

/**
 * Phone-only summary in the result colour, placed between the two panels and sticky to the bottom edge: while you
 * type it stays in view at the foot of the screen, then settles above the result. It takes no room above the tool.
 */
export const toolPeek =
  "bg-success text-success-foreground focus-visible:ring-ring sticky bottom-2 z-10 flex min-h-12 items-center gap-2 rounded-lg px-3.5 shadow-lg outline-none focus-visible:ring-[3px] md:hidden"

/** Last block of a panel, pinned to the bottom so both panels end on the same line. */
export const toolPanelFoot = "mt-auto"
